import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useMountEffect } from '../../../hooks/useMountEffect';
import { useStagePlayer } from './useStagePlayer';

/* Chapter 01 — AI guest collection, shot like a product film, behaving like
   the product.

   An avatar queue across the top shows EVERYONE the assistant is messaging.
   WhatsApp chats enter and leave in 3D, one per contributor, and each
   conversation is the real mechanics in words: the assistant opens by naming
   the person's seat allowance, collects the names they reply with, and
   confirms each batch with seats used. Names fly across the floor into the
   guest-list UI on the right, which records every arrival and WHO added it.
   Kemi repeats a name uncle bala already gave (merged, count unmoved), and
   aunty ronke runs past her two seats — the extra name bounces and the
   assistant escalates it to the planner, in the chat, like it really does. */

const PANEL_EASE = [0.23, 1, 0.32, 1] as const;

type FlyName = { name: string; merged?: boolean; blocked?: boolean };
type Contact = {
    id: string;
    initial: string;
    label: string;
    seats: number;
    ask: string;
    reply: string;
    names: FlyName[];
    confirm: string;
};

const CONTACTS: Contact[] = [
    {
        id: 'mummy',
        initial: 'M',
        label: 'mummy',
        seats: 50,
        ask: 'hello! i’m collecting your guest list for adaeze’s wedding. you have 50 seats.',
        reply: 'ah, nice! add chief & mrs adeyemi, and halima sadiq',
        names: [{ name: 'chief & mrs adeyemi' }, { name: 'halima sadiq' }],
        confirm: 'added ✓ that’s 2 of your 50 seats used.',
    },
    {
        id: 'bala',
        initial: 'B',
        label: 'uncle bala',
        seats: 30,
        ask: 'hello! i’m collecting the groom’s side of the list. you have 30 seats.',
        reply: 'no wahala. chief adebayo and bola lawal',
        names: [{ name: 'chief adebayo' }, { name: 'bola lawal' }],
        confirm: 'added ✓ 2 of your 30 seats used.',
    },
    {
        id: 'kemi',
        initial: 'K',
        label: 'kemi',
        seats: 10,
        ask: 'hi kemi! adaeze said you hold the friends’ list. you have 10 seats.',
        reply: 'yes o! add tunde, amara… and chief adebayo',
        names: [
            { name: 'tunde bakare' },
            { name: 'amara obi' },
            { name: 'chief adebayo', merged: true },
        ],
        confirm: 'added tunde and amara ✓ chief adebayo was already on uncle bala’s list, so i merged him.',
    },
    {
        id: 'ronke',
        initial: 'R',
        label: 'aunty ronke',
        seats: 2,
        ask: 'hello! adaeze gave you 2 seats for saturday’s wedding.',
        reply: 'ah ah, only 2? ok. pastor femi, sister bisi and my neighbour',
        names: [
            { name: 'pastor femi' },
            { name: 'sister bisi' },
            { name: 'my neighbour', blocked: true },
        ],
        confirm: 'your 2 seats are now full. i’ve sent the extra name to adaeze to approve.',
    },
];

type Bubble = { id: number; who: 'bot' | 'user'; text: string };
type Row = { id: number; name: string; via: string; merged: boolean };

const FLY_FROM = { x: 168, y: 168 };
const FLY_TO = { x: 262, y: 110 };
const FLIGHT_MS = 700;
const BASE_COUNT = 231;

const FINAL_ROWS: Row[] = [
    { id: 1, name: 'tunde bakare', via: 'by kemi', merged: false },
    { id: 2, name: 'amara obi', via: 'by kemi', merged: false },
    { id: 3, name: 'chief adebayo', via: 'merged', merged: true },
    { id: 4, name: 'pastor femi', via: 'by aunty ronke', merged: false },
    { id: 5, name: 'sister bisi', via: 'by aunty ronke', merged: false },
];

const GuestCollectionScene: React.FC = () => {
    const reduced = useReducedMotion();
    const player = useStagePlayer();

    const last = CONTACTS[CONTACTS.length - 1];
    const [active, setActive] = useState<number>(reduced ? CONTACTS.length - 1 : -1);
    const [done, setDone] = useState<number>(reduced ? CONTACTS.length - 1 : 0);
    const [bubbles, setBubbles] = useState<Bubble[]>(
        reduced
            ? [
                  { id: 1, who: 'user', text: last.reply },
                  { id: 2, who: 'bot', text: last.confirm },
              ]
            : [],
    );
    const [typing, setTyping] = useState(false);
    const [rows, setRows] = useState<Row[]>(reduced ? FINAL_ROWS : []);
    const [flight, setFlight] = useState<{
        id: number;
        name: string;
        blocked: boolean;
    } | null>(null);

    useMountEffect(() => {
        if (reduced) return undefined;
        let nextId = 1;
        const take = () => {
            const id = nextId;
            nextId += 1;
            return id;
        };
        return player.run(async (h) => {
            while (h.alive()) {
                await h.gate();
                setActive(-1);
                setDone(0);
                setBubbles([]);
                setTyping(false);
                setRows([]);
                setFlight(null);
                await h.wait(650);

                for (let c = 0; c < CONTACTS.length; c += 1) {
                    const person = CONTACTS[c];
                    if (!h.alive()) return;
                    await h.gate();

                    setBubbles([]);
                    setActive(c);
                    await h.wait(700);

                    setTyping(true);
                    await h.wait(800);
                    setTyping(false);
                    setBubbles((b) => [
                        ...b,
                        { id: take(), who: 'bot', text: person.ask },
                    ]);
                    await h.wait(1250);
                    if (!h.alive()) return;

                    setBubbles((b) => [
                        ...b,
                        { id: take(), who: 'user', text: person.reply },
                    ]);
                    await h.wait(950);

                    for (const entry of person.names) {
                        if (!h.alive()) return;
                        await h.gate();
                        setFlight({
                            id: take(),
                            name: entry.name,
                            blocked: Boolean(entry.blocked),
                        });
                        await h.wait(FLIGHT_MS);
                        if (!h.alive()) return;
                        setFlight(null);
                        if (!entry.blocked) {
                            setRows((r) => [
                                ...r,
                                {
                                    id: take(),
                                    name: entry.name,
                                    via: entry.merged ? 'merged' : `by ${person.label}`,
                                    merged: Boolean(entry.merged),
                                },
                            ]);
                        }
                        await h.wait(entry.merged || entry.blocked ? 550 : 280);
                    }

                    setTyping(true);
                    await h.wait(700);
                    setTyping(false);
                    setBubbles((b) => [
                        ...b,
                        { id: take(), who: 'bot', text: person.confirm },
                    ]);
                    await h.wait(1650);
                    setDone(c + 1);
                }

                await h.wait(3400);
            }
        });
    });

    const count = BASE_COUNT + rows.filter((r) => !r.merged).length;
    const visibleRows = rows.slice(-5);
    const visibleBubbles = bubbles.slice(-2);

    return (
        <motion.div
            className="ngl-scene"
            style={{ minHeight: 356 }}
            {...player.viewportProps}
            role="img"
            aria-label="Comtev's AI assistant messages every list-holder on WhatsApp in turn: mummy with 50 seats, uncle bala with 30, kemi with 10 and aunty ronke with 2 — it opens each chat by naming their seat allowance, collects the names they reply with, and confirms seats used. Each name flies into a live guest list that records who added it, a duplicate is merged without moving the count, and when aunty ronke exceeds her two seats the extra name is bounced to the planner for approval."
        >
            {/* everyone the assistant is working, at a glance */}
            <div
                className="ngl-scene-queue"
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    top: 12,
                    left: 16,
                    display: 'flex',
                    gap: 6,
                    zIndex: 3,
                }}
            >
                {CONTACTS.map((person, i) => (
                    <span
                        key={person.id}
                        className="ngl-scene-avatar"
                        style={{
                            width: 22,
                            height: 22,
                            background: i === active ? '#008069' : '#fff',
                            color: i === active ? '#fff' : '#008069',
                            border: '1px solid rgba(26,23,19,0.18)',
                            opacity: i < done || i === active ? 1 : 0.4,
                            transition: 'all .25s ease',
                        }}
                    >
                        {i < done && i !== active ? '✓' : person.initial}
                    </span>
                ))}
            </div>

            {/* the guest list stays on set for the whole film */}
            <motion.div
                className="ngl-scene-card ngl-scene-list"
                initial={false}
                animate={
                    reduced
                        ? { rotateY: -9, rotateX: 2 }
                        : { rotateY: -9, rotateX: 2, y: [0, -5, 0] }
                }
                transition={
                    reduced
                        ? undefined
                        : { y: { duration: 7, repeat: Infinity, ease: 'easeInOut' } }
                }
            >
                <div className="ngl-scene-list-head">
                    <span className="ngl-scene-list-title">the guest list</span>
                    <span className="ngl-scene-list-count">
                        <motion.span
                            key={count}
                            style={{ display: 'inline-block' }}
                            initial={reduced ? false : { y: -7, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.28, ease: PANEL_EASE }}
                        >
                            {count}
                        </motion.span>{' '}
                        names
                    </span>
                </div>
                <AnimatePresence initial={false}>
                    {visibleRows.map((row) => (
                        <motion.div
                            key={row.id}
                            className={`ngl-scene-row${row.merged ? ' is-merged' : ''}`}
                            initial={reduced ? false : { opacity: 0, y: 10, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={reduced ? undefined : { opacity: 0, y: -8 }}
                            transition={{ duration: 0.3, ease: PANEL_EASE }}
                        >
                            <span className="ngl-scene-row-name">{row.name}</span>
                            <span
                                className="ngl-scene-row-via"
                                style={
                                    row.merged
                                        ? { color: 'var(--ngl-ember-deep)' }
                                        : undefined
                                }
                            >
                                {row.via}
                            </span>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </motion.div>

            {/* chat windows enter and leave like shots */}
            <AnimatePresence mode="wait">
                {active >= 0 && (
                    <motion.div
                        key={CONTACTS[active].id}
                        className="ngl-scene-card ngl-scene-chat"
                        style={{ top: 44 }}
                        initial={
                            reduced
                                ? false
                                : {
                                      opacity: 0,
                                      x: -74,
                                      rotateY: 34,
                                      rotateX: 3,
                                      scale: 0.88,
                                      filter: 'blur(3px)',
                                  }
                        }
                        animate={{
                            opacity: 1,
                            x: 0,
                            rotateY: 9,
                            rotateX: 3,
                            scale: 1,
                            filter: 'blur(0px)',
                        }}
                        exit={
                            reduced
                                ? undefined
                                : {
                                      opacity: 0,
                                      x: -60,
                                      rotateY: 26,
                                      scale: 0.9,
                                      filter: 'blur(3px)',
                                  }
                        }
                        transition={{ duration: 0.55, ease: PANEL_EASE }}
                    >
                        <div className="ngl-scene-chat-head">
                            <span className="ngl-scene-avatar" aria-hidden="true">
                                {CONTACTS[active].initial}
                            </span>
                            <span>
                                <span
                                    className="ngl-scene-chat-name"
                                    style={{ display: 'block' }}
                                >
                                    {CONTACTS[active].label}
                                </span>
                                <span className="ngl-scene-chat-status">online</span>
                            </span>
                        </div>
                        <div className="ngl-scene-chat-feed">
                            <AnimatePresence initial={false}>
                                {visibleBubbles.map((bubble) => (
                                    <motion.div
                                        key={bubble.id}
                                        className={`ngl-ch-bubble is-${bubble.who}`}
                                        initial={
                                            reduced
                                                ? false
                                                : { opacity: 0, y: 12, scale: 0.97 }
                                        }
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={
                                            reduced ? undefined : { opacity: 0, y: -10 }
                                        }
                                        transition={{
                                            duration: 0.3,
                                            ease: PANEL_EASE,
                                        }}
                                    >
                                        {bubble.who === 'bot' && (
                                            <span
                                                className="ngl-wa-sender"
                                                aria-hidden="true"
                                            >
                                                comtev ai ✦
                                            </span>
                                        )}
                                        {bubble.text}
                                        {bubble.who === 'user' && (
                                            <span
                                                className="ngl-wa-ticks"
                                                aria-hidden="true"
                                            >
                                                ✓✓
                                            </span>
                                        )}
                                    </motion.div>
                                ))}
                                {typing && (
                                    <motion.div
                                        key="typing"
                                        className="ngl-ch-typing"
                                        aria-label="typing"
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.22 }}
                                    >
                                        <span />
                                        <span />
                                        <span />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* a name crossing the floor */}
            {!reduced && flight && (
                <motion.span
                    key={flight.id}
                    className={`ngl-fly${flight.blocked ? ' is-blocked' : ''}`}
                    style={{ left: FLY_FROM.x, top: FLY_FROM.y, zIndex: 5 }}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={
                        flight.blocked
                            ? {
                                  opacity: [0, 1, 1, 0],
                                  scale: [0.85, 1, 1, 0.9],
                                  y: [0, -10, -10, -16],
                              }
                            : {
                                  x: FLY_TO.x - FLY_FROM.x,
                                  y: [0, -26, FLY_TO.y - FLY_FROM.y],
                                  opacity: [0, 1, 1, 0],
                                  scale: [0.85, 1, 1, 0.7],
                              }
                    }
                    transition={{
                        duration: FLIGHT_MS / 1000,
                        ease: 'easeInOut',
                        times: [0, 0.2, 0.8, 1],
                    }}
                    aria-hidden="true"
                >
                    {flight.blocked ? 'over the limit' : flight.name}
                </motion.span>
            )}

            <span className="ngl-scene-caption" aria-hidden="true">
                one AI assistant · every chat · one list
            </span>

            {!reduced && (
                <button
                    type="button"
                    className="ngl-stage-pause"
                    aria-label={player.paused ? 'Play animation' : 'Pause animation'}
                    onClick={player.togglePaused}
                >
                    {player.paused ? (
                        <svg viewBox="0 0 10 10">
                            <path d="M2 1l7 4-7 4z" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 10 10">
                            <path d="M2 1h2.4v8H2zM5.6 1H8v8H5.6z" />
                        </svg>
                    )}
                </button>
            )}
        </motion.div>
    );
};

export default GuestCollectionScene;
